import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import type { NewsItem, SearchSettings, KeywordTabData, KeywordGroup } from '../types/news.types'

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// News Store State Interface
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

interface NewsState {
  // Keywords
  keywords: string[]
  activeKeyword: string | null

  // Tab data
  tabs: Record<string, KeywordTabData>

  // Search settings
  settings: SearchSettings

  // Groups
  groups: KeywordGroup[]

  hasPerformedFirstSearch: boolean

  // Actions - Keywords
  setKeywords: (keywords: string[]) => void
  addKeyword: (keyword: string) => void
  removeKeyword: (keyword: string) => void
  setActiveKeyword: (keyword: string | null) => void

  // Actions - Tab data
  setTabData: (keyword: string, items: NewsItem[], total: number) => void
  setTabLoading: (keyword: string, isLoading: boolean) => void
  setTabError: (keyword: string, error: string | null) => void
  setTabPage: (keyword: string, page: number) => void
  clearTabData: (keyword: string) => void

  // Actions - Settings
  updateSettings: (settings: Partial<SearchSettings>) => void

  // Actions - Groups
  createGroup: (name: string, color: string) => void
  updateGroup: (id: string, updates: Partial<KeywordGroup>) => void
  deleteGroup: (id: string) => void
  addKeywordToGroup: (groupId: string, keyword: string) => void
  removeKeywordFromGroup: (groupId: string, keyword: string) => void
  toggleGroupCollapse: (id: string) => void
  reorderGroups: (groups: KeywordGroup[]) => void

  markFirstSearchComplete: () => void

  // Actions - Reset
  reset: () => void
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Initial State
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const initialSettings: SearchSettings = {
  display: 24,
  sort: 'sim',
  enableAISummary: false, // Default: AI summary disabled
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// News Store
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export const useNewsStore = create<NewsState>()(
  devtools(
    persist(
      (set) => ({
        // Initial state
        keywords: [],
        activeKeyword: null,
        tabs: {},
        settings: initialSettings,
        groups: [],
        hasPerformedFirstSearch: false,

        // Keyword actions
        setKeywords: (keywords) =>
          set({ keywords, activeKeyword: keywords[0] || null }, false, 'setKeywords'),

        addKeyword: (keyword) =>
          set(
            (state) => {
              if (state.keywords.includes(keyword)) return state
              return {
                keywords: [...state.keywords, keyword],
                activeKeyword: state.activeKeyword || keyword,
              }
            },
            false,
            'addKeyword'
          ),

        removeKeyword: (keyword) =>
          set(
            (state) => {
              const newKeywords = state.keywords.filter((k) => k !== keyword)
              const newTabs = { ...state.tabs }
              delete newTabs[keyword]

              return {
                keywords: newKeywords,
                tabs: newTabs,
                activeKeyword:
                  state.activeKeyword === keyword ? newKeywords[0] || null : state.activeKeyword,
              }
            },
            false,
            'removeKeyword'
          ),

        setActiveKeyword: (keyword) => set({ activeKeyword: keyword }, false, 'setActiveKeyword'),

        // Tab data actions
        setTabData: (keyword, items, total) =>
          set(
            (state) => ({
              tabs: {
                ...state.tabs,
                [keyword]: {
                  keyword,
                  items,
                  total,
                  currentPage: state.tabs[keyword]?.currentPage || 1,
                  isLoading: false,
                  error: null,
                },
              },
            }),
            false,
            'setTabData'
          ),

        setTabLoading: (keyword, isLoading) =>
          set(
            (state) => ({
              tabs: {
                ...state.tabs,
                [keyword]: {
                  ...state.tabs[keyword],
                  keyword,
                  items: state.tabs[keyword]?.items || [],
                  total: state.tabs[keyword]?.total || 0,
                  currentPage: state.tabs[keyword]?.currentPage || 1,
                  isLoading,
                  error: null,
                },
              },
            }),
            false,
            'setTabLoading'
          ),

        setTabError: (keyword, error) =>
          set(
            (state) => ({
              tabs: {
                ...state.tabs,
                [keyword]: {
                  ...state.tabs[keyword],
                  keyword,
                  items: state.tabs[keyword]?.items || [],
                  total: state.tabs[keyword]?.total || 0,
                  currentPage: state.tabs[keyword]?.currentPage || 1,
                  isLoading: false,
                  error,
                },
              },
            }),
            false,
            'setTabError'
          ),

        setTabPage: (keyword, page) =>
          set(
            (state) => ({
              tabs: {
                ...state.tabs,
                [keyword]: {
                  ...state.tabs[keyword],
                  keyword,
                  items: state.tabs[keyword]?.items || [],
                  total: state.tabs[keyword]?.total || 0,
                  currentPage: page,
                  isLoading: state.tabs[keyword]?.isLoading || false,
                  error: state.tabs[keyword]?.error || null,
                },
              },
            }),
            false,
            'setTabPage'
          ),

        clearTabData: (keyword) =>
          set(
            (state) => {
              const newTabs = { ...state.tabs }
              delete newTabs[keyword]
              return { tabs: newTabs }
            },
            false,
            'clearTabData'
          ),

        // Settings actions
        updateSettings: (newSettings) =>
          set(
            (state) => ({
              settings: { ...state.settings, ...newSettings },
            }),
            false,
            'updateSettings'
          ),

        // Group actions
        createGroup: (name, color) =>
          set(
            (state) => ({
              groups: [
                ...state.groups,
                {
                  id: crypto.randomUUID(),
                  name,
                  color,
                  keywords: [],
                  collapsed: false,
                },
              ],
            }),
            false,
            'createGroup'
          ),

        updateGroup: (id, updates) =>
          set(
            (state) => ({
              groups: state.groups.map((group) =>
                group.id === id ? { ...group, ...updates } : group
              ),
            }),
            false,
            'updateGroup'
          ),

        deleteGroup: (id) =>
          set(
            (state) => ({
              groups: state.groups.filter((group) => group.id !== id),
            }),
            false,
            'deleteGroup'
          ),

        addKeywordToGroup: (groupId, keyword) =>
          set(
            (state) => ({
              groups: state.groups.map((group) =>
                group.id === groupId ? { ...group, keywords: [...group.keywords, keyword] } : group
              ),
            }),
            false,
            'addKeywordToGroup'
          ),

        removeKeywordFromGroup: (groupId, keyword) =>
          set(
            (state) => ({
              groups: state.groups.map((group) =>
                group.id === groupId
                  ? { ...group, keywords: group.keywords.filter((k) => k !== keyword) }
                  : group
              ),
            }),
            false,
            'removeKeywordFromGroup'
          ),

        toggleGroupCollapse: (id) =>
          set(
            (state) => ({
              groups: state.groups.map((group) =>
                group.id === id ? { ...group, collapsed: !group.collapsed } : group
              ),
            }),
            false,
            'toggleGroupCollapse'
          ),

        reorderGroups: (groups) => set({ groups }, false, 'reorderGroups'),

        markFirstSearchComplete: () =>
          set({ hasPerformedFirstSearch: true }, false, 'markFirstSearchComplete'),

        // Reset action
        reset: () =>
          set(
            {
              keywords: [],
              activeKeyword: null,
              tabs: {},
              settings: initialSettings,
              groups: [],
              hasPerformedFirstSearch: false,
            },
            false,
            'reset'
          ),
      }),
      {
        name: 'news-store',
        // `keywords` is persisted so a returning user still tracks what they set up.
        // `tabs` deliberately is not — it holds full article payloads.
        partialize: (state) => ({
          keywords: state.keywords,
          settings: state.settings,
          groups: state.groups,
          hasPerformedFirstSearch: state.hasPerformedFirstSearch,
        }),
      }
    ),
    { name: 'news-store' }
  )
)

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Selectors
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export const selectActiveTab = (state: NewsState) =>
  state.activeKeyword ? state.tabs[state.activeKeyword] : null

export const selectTabByKeyword = (keyword: string) => (state: NewsState) => state.tabs[keyword]
